import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { ANNUAL_INCENTIVES_AWARD_CODES } from "../../common/constants/market-constants";
import { ANNUAL_INCENTIVES_PATHNAMES, ROUTE_PATHS } from "../../common/enums/routing-enums";
import { AWARD_CODES } from "../../common/enums/award-codes";
import { setShellLoading, setUser } from "../../reducers/boot";
import { ShellConfigApiResponse } from "./mybizShellAPI.types";
import { useGetMyBizShellConfigQuery } from "./mybizShellAPI";

export const ShellConfigData = ({ pathname }: { pathname: string }) => {
  const { selectedPeriod: bonusPeriod } = useAppSelector((state) => state.period);
  const { user, configuration } = useAppSelector((state) => state.boot);
  const dispatch = useAppDispatch();
  const faaEligibilityWindow = configuration.faaEligibilityWindow;
  const {
    data: shellConfigData,
    isLoading: isQueryLoading,
    isFetching,
    isError,
  } = useGetMyBizShellConfigQuery(
    {
      aff: user.aff,
      abo: user.abo,
      period: bonusPeriod,
      faaEligibilityWindow: faaEligibilityWindow,
      optionalPeriodRange: configuration.optionalPeriodRange,
    },
    { skip: !user.aff },
  );

  const getAnnualPeriods = () => {
    return (
      shellConfigData?.periods?.performanceYears.map((period) => Number(period.display)) || [
        Number(`${bonusPeriod.toString().slice(0, 4)}08`),
      ]
    );
  };

  const getAvailableAnnualAwards = () => {
    const nums = shellConfigData?.eligibleIncentives
      ?.filter(({ awardNumber }) => ANNUAL_INCENTIVES_AWARD_CODES.includes(awardNumber))
      .map(({ awardNumber }) => awardNumber);

    return nums?.length ? nums : [];
  };

  const availableAnnualAwards = getAvailableAnnualAwards();

  const [isLoading, setIsLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(false);
  const [data, setData] = useState<any>(null);
  const checkForAnnualComingSoon = useCallback(
    (shellConfig: ShellConfigApiResponse) => {
      const selectedIncentive = shellConfig.eligibleIncentives.slice().find((incentives) => {
        const findKey = (Object.keys(ROUTE_PATHS) as (keyof typeof ROUTE_PATHS)[]).find((key) => {
          return ROUTE_PATHS[key] === pathname;
        });

        const awardCodesArr: any = AWARD_CODES;
        return findKey
          ? (incentives.programActive || incentives.comingSoon) &&
              awardCodesArr[findKey] === incentives.awardNumber &&
              incentives.bonusPeriod === Number(bonusPeriod)
          : false;
      });

      // logic to set coming soon for annual performance dashboard, checking all the annual incentives if any one of them is coming soon
      let isComingSoon = false;

      if (pathname === ROUTE_PATHS.ANNUAL_PERFORMANCE) {
        const filteredincentives = shellConfig.eligibleIncentives
          .slice()
          .filter(
            (incentive) =>
              ANNUAL_INCENTIVES_AWARD_CODES.includes(incentive.awardNumber) &&
              incentive.bonusPeriod === Number(bonusPeriod),
          );
        isComingSoon = filteredincentives.some((incentive) => incentive.comingSoon);
      } else if (selectedIncentive) {
        isComingSoon = selectedIncentive?.comingSoon || false;
      }

      return { isComingSoon };
    },
    [pathname, bonusPeriod],
  );

  // if loading or fetching set loading flag to true
  useEffect(() => {
    if (isQueryLoading || isFetching) {
      setIsLoading(true);
      dispatch(setShellLoading({ isLoading: isQueryLoading, isFetching }));
    }
  }, [isQueryLoading, isFetching, dispatch]);

  useEffect(() => {
    if (isError) {
      setErrorStatus(true);
      dispatch(setShellLoading({ isLoading: false, isFetching: false }));
    }
  }, [shellConfigData, isError, dispatch]);

  //once loading has finished and data is resolved, save to state and set loading to false
  useEffect(() => {
    if (!isQueryLoading && !isFetching && !isError && shellConfigData) {
      const countryCode = shellConfigData.aboInfo.isoCountryCode;
      if (ANNUAL_INCENTIVES_PATHNAMES.includes(pathname)) {
        const comingSoonData = checkForAnnualComingSoon(JSON.parse(JSON.stringify(shellConfigData)));
        setData({
          ...shellConfigData,
          isComingSoon: comingSoonData.isComingSoon,
        });
        setIsLoading(false);
        dispatch(setUser({ user: { isoCountryCode: countryCode } }));
        dispatch(setShellLoading({ isLoading: false, isFetching: false }));
      } else {
        setData({ ...shellConfigData, isComingSoon: null });
        setIsLoading(false);
        dispatch(setUser({ user: { isoCountryCode: countryCode } }));
        dispatch(setShellLoading({ isLoading: false, isFetching: false }));
      }
      setErrorStatus(false);
      dispatch(setShellLoading({ isLoading: false, isFetching: false }));
      sessionStorage.setItem("tealiumCurrentAwardRank", `${shellConfigData.aboInfo.currentAwardRank}`);
      sessionStorage.setItem("tealiumGarAwardTrackingLevel", `${shellConfigData.aboInfo.garAwardTrackingLevel}`);
    }
  }, [shellConfigData, isError, isQueryLoading, isFetching, bonusPeriod, pathname, checkForAnnualComingSoon, dispatch]);

  return { isLoading, data, errorStatus };
};
